import { htmlToMarkdown } from '@markbird/markdown/html-to-markdown';

/**
 * Evernote exports (`.enex`), read in the browser.
 *
 * An `.enex` file is XML: one `<note>` per note, whose `<content>` holds an
 * HTML document as CDATA, plus `<resource>` elements carrying attachments as
 * base64. Everything worth keeping — the title, the writing, the tags, when it
 * was made — is in there, and all of it can be turned into a Markdown file.
 *
 * The one honest compromise is images. Evernote refers to an attachment with
 * `<en-media hash="…">`, an MD5 of the bytes, and browsers have no MD5. Media
 * elements are therefore matched to resources of the same type **in document
 * order**, which is what they are in every export I could find; a note whose
 * images cannot be matched keeps its text and loses the picture rather than
 * getting someone else's.
 */

export interface EnexResource {
  mime: string;
  /** The attachment's bytes, ready for the image store. */
  data: Uint8Array;
  fileName?: string;
}

export interface EnexNote {
  title: string;
  /** The note's HTML, as Evernote wrote it. */
  html: string;
  tags: string[];
  createdAt: number | null;
  updatedAt: number | null;
  resources: EnexResource[];
}

/** True for an Evernote export file. */
export function isEnexFile(file: File): boolean {
  return /\.enex$/i.test(file.name) || file.type === 'application/enex+xml';
}

/** `20240115T093000Z` — Evernote's own date format. */
export function parseEnexDate(value: string | null | undefined): number | null {
  const match = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z?$/.exec((value ?? '').trim());
  if (!match) return null;
  const [, y, mo, d, h, mi, s] = match;
  return Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s));
}

function base64ToBytes(base64: string): Uint8Array {
  const clean = base64.replace(/\s+/g, '');
  const binary = atob(clean);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/** Read an export into notes. Throws only if the file is not an export at all. */
export function parseEnex(xml: string): EnexNote[] {
  const doc = new DOMParser().parseFromString(xml, 'text/xml');
  if (doc.querySelector('parsererror') || !doc.querySelector('en-export')) {
    throw new Error('Not an Evernote export.');
  }

  return [...doc.querySelectorAll('note')].map((note) => {
    const text = (selector: string) => note.querySelector(selector)?.textContent ?? '';
    const resources: EnexResource[] = [];
    for (const resource of note.querySelectorAll('resource')) {
      const encoded = resource.querySelector('data')?.textContent ?? '';
      if (!encoded.trim()) continue;
      try {
        resources.push({
          mime: resource.querySelector('mime')?.textContent ?? 'application/octet-stream',
          data: base64ToBytes(encoded),
          fileName: resource.querySelector('file-name')?.textContent ?? undefined,
        });
      } catch {
        // A resource that is not valid base64 is skipped; the note is not.
      }
    }

    return {
      title: text('title').trim() || 'Untitled',
      html: note.querySelector('content')?.textContent ?? '',
      tags: [...note.querySelectorAll('tag')]
        .map((tag) => tag.textContent?.trim() ?? '')
        .filter(Boolean),
      createdAt: parseEnexDate(text('created')),
      updatedAt: parseEnexDate(text('updated')),
      resources,
    };
  });
}

function escapeYaml(value: string): string {
  return `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
}

/**
 * One note as Markdown.
 *
 * `storeImage` puts an attachment somewhere and returns the reference to use —
 * in the app that is the local image store (`mbimg://…`), and in tests it is
 * whatever the test wants to check.
 */
export async function enexNoteToMarkdown(
  note: EnexNote,
  storeImage?: (resource: EnexResource) => Promise<string>,
): Promise<{ title: string; content: string; createdAt: number | null }> {
  const doc = new DOMParser().parseFromString(note.html || '<en-note></en-note>', 'text/html');

  // Attachments, in the order the note refers to them.
  // `<en-media/>` is not a known void element, so the HTML parser nests
  // whatever follows it inside. Its children are kept when it is replaced.
  const media = [...doc.querySelectorAll('en-media')];
  const unused = [...note.resources];
  for (const element of media) {
    const type = element.getAttribute('type') ?? '';
    const index = unused.findIndex((resource) => resource.mime === type);
    const resource = index >= 0 ? unused.splice(index, 1)[0] : undefined;
    const reference = resource && storeImage ? await storeImage(resource) : null;
    if (reference && type.startsWith('image/')) {
      const img = doc.createElement('img');
      img.setAttribute('src', reference);
      img.setAttribute('alt', resource?.fileName ?? '');
      element.replaceWith(img, ...element.childNodes);
    } else if (reference) {
      // Not an image: a link is more use than a broken picture.
      const link = doc.createElement('a');
      link.setAttribute('href', reference);
      link.textContent = resource?.fileName ?? type;
      element.replaceWith(link, ...element.childNodes);
    } else {
      element.replaceWith(...element.childNodes);
    }
  }

  const body = htmlToMarkdown(doc.body);
  const frontmatter = [
    '---',
    'source: "evernote"',
    ...(note.tags.length ? [`tags: [${note.tags.map((t) => escapeYaml(t)).join(', ')}]`] : []),
    ...(note.createdAt ? [`created: ${new Date(note.createdAt).toISOString().slice(0, 10)}`] : []),
    '---',
    '',
    '',
  ].join('\n');

  return {
    title: note.title,
    content: `${frontmatter}# ${note.title}\n\n${body}\n`,
    createdAt: note.createdAt,
  };
}
